/**
 * R-236 B3 — Historical roll-up view for the Staff Monitor.
 *
 * When `operatorId` is present in the URL → renders OperatorDetail (B3b stub).
 * When absent → renders the full roll-up: range presets, operator + job-type
 * filters, breakdown table, CSV export, and print report.
 *
 * Design: Industrial Core (DESIGN.md). Tokens only — no inline styles.
 * Accent: Safety Orange #ea580c (--color-primary).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "@/lib/api-client";
import { useJobTypes } from "@/features/admin/hooks/useJobTypes";
import { CsvExportButton } from "@/features/analytics/components/CsvExportButton";
import { OperatorDetail } from "./OperatorDetail";
import { addOneDayIso, daysAgoIso, presetRange, todayIso } from "./dateRange";
import type { RangePreset } from "./dateRange";
import "../admin.css";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EnrichedHistoricalRow {
  operatorId: string;
  jobTypeId: string;
  displayName: string;
  jobTypeName: string;
  totalDocs: number;
  docsPerMin: number;
  pagesPerMin: number;
  keysPerMin: number;
  corrections: number;
  avgTimePerField: number;
}

const PRESET_LABELS: Record<RangePreset, string> = {
  today: "Today",
  "7d": "7d",
  "30d": "30d",
  custom: "Custom",
};

// ---------------------------------------------------------------------------
// HistoricalReport
// ---------------------------------------------------------------------------

export function HistoricalReport() {
  const [searchParams, setSearchParams] = useSearchParams();

  const operatorId = searchParams.get("operatorId") ?? undefined;

  // ── filter state ──────────────────────────────────────────────────────────
  const [preset, setPreset] = useState<RangePreset>("30d");
  const [from, setFrom] = useState<string>(daysAgoIso(29));
  const [to, setTo] = useState<string>(todayIso());
  const [filterOperatorId, setFilterOperatorId] = useState<string>("");
  const [filterJobTypeId, setFilterJobTypeId] = useState<string>("");

  // ── data ──────────────────────────────────────────────────────────────────
  const [rows, setRows] = useState<EnrichedHistoricalRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { items: jobTypes } = useJobTypes();

  // Unique operators from the unfiltered result (used to populate the operator
  // filter dropdown). We keep a separate "all-operators" list derived from the
  // last unfiltered fetch.
  const [allOperators, setAllOperators] = useState<
    Array<{ operatorId: string; displayName: string }>
  >([]);

  // ── fetch ─────────────────────────────────────────────────────────────────

  const fetchData = useCallback(
    async (opts: {
      from: string;
      to: string;
      operatorId?: string;
      jobTypeId?: string;
    }) => {
      setIsLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams({ from: opts.from, to: opts.to });
        if (opts.operatorId) qs.set("operatorId", opts.operatorId);
        if (opts.jobTypeId) qs.set("jobTypeId", opts.jobTypeId);
        const data = await api.get<{ rows: EnrichedHistoricalRow[] }>(
          `/ops/staff/historical?${qs.toString()}`,
        );
        setRows(data.rows);
        // Populate operator dropdown from any unfiltered fetch (no operatorId filter)
        if (!opts.operatorId) {
          const seen = new Map<string, string>();
          for (const r of data.rows) {
            if (!seen.has(r.operatorId)) seen.set(r.operatorId, r.displayName);
          }
          setAllOperators(
            Array.from(seen.entries()).map(([operatorId, displayName]) => ({
              operatorId,
              displayName,
            })),
          );
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  // `to` (state) is the user-facing INCLUSIVE end date; the API expects an
  // EXCLUSIVE upper bound, so the wire value is end-date + 1 day.
  const apiTo = addOneDayIso(to);

  // Re-fetch whenever filters change. We send `apiTo` (exclusive) as the
  // `to` query param so the selected end date is fully included.
  useEffect(() => {
    fetchData({
      from,
      to: apiTo,
      operatorId: filterOperatorId || undefined,
      jobTypeId: filterJobTypeId || undefined,
    });
  }, [from, apiTo, filterOperatorId, filterJobTypeId, fetchData]);

  // ── preset buttons ────────────────────────────────────────────────────────

  function applyPreset(p: RangePreset) {
    setPreset(p);
    if (p !== "custom") {
      const range = presetRange(p);
      setFrom(range.from);
      setTo(range.to);
    }
  }

  // ── drill-in navigation ───────────────────────────────────────────────────

  function drillIn(rowOperatorId: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("operatorId", rowOperatorId);
        next.set("tab", "historical");
        return next;
      },
      { replace: false },
    );
  }

  function clearDrillIn() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("operatorId");
        next.set("tab", "historical");
        return next;
      },
      { replace: false },
    );
  }

  // ── CSV params ────────────────────────────────────────────────────────────

  // CSV uses the SAME exclusive `apiTo` as the fetch so the downloaded file
  // covers exactly the same window the on-screen table shows.
  const csvParams = useMemo<Record<string, string>>(() => {
    const p: Record<string, string> = { from, to: apiTo };
    if (filterOperatorId) p.operatorId = filterOperatorId;
    if (filterJobTypeId) p.jobTypeId = filterJobTypeId;
    return p;
  }, [from, apiTo, filterOperatorId, filterJobTypeId]);

  // ── drill-in: show OperatorDetail ─────────────────────────────────────────
  if (operatorId) {
    return (
      <OperatorDetail
        operatorId={operatorId}
        from={from}
        to={to}
        onBack={clearDrillIn}
      />
    );
  }

  // Human-readable summary of the active range + filters. Shown only on the
  // printout (via @media print) so the page describes exactly what window and
  // filters it covers. `to` is the inclusive end date the user picked.
  const rangeLabel = preset === "custom" ? `${from} – ${to}` : `${PRESET_LABELS[preset]} (${from} – ${to})`;
  const operatorLabel =
    allOperators.find((op) => op.operatorId === filterOperatorId)?.displayName ?? "All operators";
  const jobTypeLabel = jobTypes.find((jt) => jt.id === filterJobTypeId)?.name ?? "All job types";

  // ── roll-up view ──────────────────────────────────────────────────────────
  // `.hist-print-region` is the print-isolation anchor (see @media print in
  // admin.css): when window.print() fires, only this subtree is visible.
  return (
    <div data-testid="historical-rollup" className="hist-print-region">
      {/* Controls bar — layout via .hist-controls in admin.css. Hidden on print. */}
      <div className="hist-controls">
        {/* Range presets */}
        <div className="hist-preset-group">
          {(["today", "7d", "30d", "custom"] as const).map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={preset === p}
              className={`admin-btn admin-btn--secondary${preset === p ? " hist-preset--active" : ""}`}
              onClick={() => applyPreset(p)}
            >
              {PRESET_LABELS[p]}
            </button>
          ))}
        </div>

        {/* Custom date range inputs — only shown when preset = custom */}
        {preset === "custom" && (
          <>
            <input
              type="date"
              className="rb-input hist-date-input"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              aria-label="From date"
            />
            <span className="hist-date-sep">to</span>
            <input
              type="date"
              className="rb-input hist-date-input"
              value={to}
              min={from}
              max={todayIso()}
              onChange={(e) => setTo(e.target.value)}
              aria-label="To date"
            />
          </>
        )}

        {/* Operator filter */}
        <select
          className="rb-select hist-filter-select hist-filter-select--operator"
          value={filterOperatorId}
          onChange={(e) => setFilterOperatorId(e.target.value)}
          aria-label="Operator filter"
        >
          <option value="">All operators</option>
          {allOperators.map((op) => (
            <option key={op.operatorId} value={op.operatorId}>
              {op.displayName}
            </option>
          ))}
        </select>

        {/* Job-type filter */}
        <select
          className="rb-select hist-filter-select hist-filter-select--jobtype"
          value={filterJobTypeId}
          onChange={(e) => setFilterJobTypeId(e.target.value)}
          aria-label="Job type filter"
        >
          <option value="">All job types</option>
          {jobTypes.map((jt) => (
            <option key={jt.id} value={jt.id}>
              {jt.name}
            </option>
          ))}
        </select>

        {/* Actions — pushed to far right via .hist-actions margin-left: auto */}
        <div className="hist-actions">
          <button
            type="button"
            className="admin-btn admin-btn--secondary"
            onClick={() => window.print()}
          >
            Print report
          </button>
          <CsvExportButton
            endpoint="/api/ops/staff/historical.csv"
            params={csvParams}
            filename="staff-historical.csv"
          />
        </div>
      </div>

      {/* Print-only summary — describes the range + filters the printout covers.
          Hidden on screen, revealed by @media print in admin.css. */}
      <div className="hist-print-summary" data-testid="historical-print-summary">
        <h2 className="hist-print-title">Staff Historical Report</h2>
        <p className="hist-print-meta">
          Range: {rangeLabel} · Operator: {operatorLabel} · Job type: {jobTypeLabel}
        </p>
      </div>

      {/* Content */}
      {isLoading ? (
        <div data-testid="historical-loading" className="admin-loading">
          Loading…
        </div>
      ) : error ? (
        <div className="admin-error">{error}</div>
      ) : rows.length === 0 ? (
        <div data-testid="historical-empty" className="jt-empty">
          No data for this range.
        </div>
      ) : (
        <div className="hist-table-wrap" data-testid="historical-table">
          <table className="roster-table">
            <thead>
              <tr>
                <th scope="col">Operator</th>
                <th scope="col">Job Type</th>
                <th scope="col">Total Docs</th>
                <th scope="col">Docs/min</th>
                <th scope="col">Pages/min</th>
                <th scope="col">Keys/min</th>
                <th scope="col">Corrections</th>
                <th scope="col">Avg time/field</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${row.operatorId}-${row.jobTypeId}`}
                  className="roster-row"
                  data-testid={`historical-row-${row.operatorId}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => drillIn(row.operatorId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      drillIn(row.operatorId);
                    }
                  }}
                >
                  <td>{row.displayName}</td>
                  <td>{row.jobTypeName}</td>
                  <td>{row.totalDocs.toLocaleString()}</td>
                  <td>{row.docsPerMin.toFixed(1)}</td>
                  <td>{row.pagesPerMin.toFixed(1)}</td>
                  <td>{row.keysPerMin.toFixed(0)}</td>
                  <td>{row.corrections}</td>
                  <td>{row.avgTimePerField > 0 ? `${Math.round(row.avgTimePerField)}ms` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
